/**
 * Brigade.js
 * PSP/CRE Tool v3.0 - Hierarchical Force Structure
 * 
 * Brigade class - tactical formation of 3-5 battalions
 * Typically 3,000-5,000 personnel, has organic Role 2 Basic medical facility
 * 
 * Medical Hierarchy Position:
 * - Brigade → Role 2 Basic (Forward Resuscitation)
 * - Provides advanced trauma care, limited surgery
 * - Receives casualties from battalion Role 1 facilities
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: November 26, 2025
 * Version: 3.0.1 (February 7, 2026)
 * 
 * CHANGELOG v3.0.1 (February 7, 2026):
 * - CRITICAL FIX: Added sub_units and composition_source to toJSON()
 * - These fields are set by ForceService.generateSubUnitsFromDoctrine()
 * - Required for v3.0 calculator to access doctrinal sub-units composition
 * - Without this fix, JSON.stringify strips sub_units during serialization
 */

import { BaseUnit } from './BaseUnit.js';

export class Brigade extends BaseUnit {
  /**
   * Create a Brigade
   * 
   * @param {Object} config - Brigade configuration
   * @param {string} config.id - Unique identifier
   * @param {string} config.name - Brigade name
   * @param {number} config.brigade_number - Number within division (1, 2, 3...)
   * @param {number} [config.strength=4000] - Personnel strength
   * @param {number} [config.position_feba_km=20] - Distance from FEBA (Romanian default: 20km)
   */
  constructor(config) {
    super({
      ...config,
      type: 'BRIGADE',
      strength: config.strength || 4000,
      position_feba_km: config.position_feba_km !== undefined ? config.position_feba_km : 20
    });
    
    // Brigade-specific properties
    this.brigade_number = config.brigade_number;    // Position within division (1, 2, 3)
    this.parent_division_id = null;                 // Set when added to division
    this.battalions = [];                           // Array of Battalion instances
    
    // Medical facilities
    this.role2_basic_facility = null;               // Will be linked from Tab 3
    this.role2_basic_facility_id = null;            // Unique facility ID once linked
    
    // Brigade type (affects operations)
    this.brigade_type = config.brigade_type || 'INFANTRY'; // INFANTRY, ARMOR, MECHANIZED, etc.
  }
  
  // ============================================================================
  // BATTALION MANAGEMENT METHODS
  // ============================================================================
  
  /**
   * Add battalion to this brigade
   * Establishes parent-child relationship
   * 
   * @param {Battalion} battalion - Battalion instance to add
   */
  addBattalion(battalion) {
    this.addChild(battalion);
    this.battalions.push(battalion);
    battalion.parent_brigade_id = this.id;
  }
  
  /**
   * Get all battalions in this brigade
   * 
   * @returns {Battalion[]} Array of battalion instances
   */
  getBattalions() {
    return this.battalions;
  }
  
  /**
   * Get battalion by number
   * 
   * @param {number} battalion_number - Battalion number (1, 2, 3...)
   * @returns {Battalion|null} Battalion instance or null if not found
   */
  getBattalionByNumber(battalion_number) {
    return this.battalions.find(bn => bn.battalion_number === battalion_number) || null;
  }
  
  /**
   * Get total strength of all battalions
   * 
   * @returns {number} Sum of all battalion current strengths
   */
  getTotalBattalionStrength() {
    return this.battalions.reduce((sum, bn) => sum + bn.current_strength, 0);
  }
  
  /**
   * Get brigade strength as sum of battalion strengths plus brigade HQ
   * More accurate than using brigade.current_strength directly
   * 
   * @returns {number} Total brigade strength including HQ
   */
  getAccurateStrength() {
    const battalion_total = this.getTotalBattalionStrength();
    const hq_strength = this.current_strength - this.battalions.reduce((sum, bn) => sum + bn.strength, 0);
    return battalion_total + Math.max(0, hq_strength);
  }
  
  // ============================================================================
  // FACILITY ID GENERATION
  // ============================================================================
  
  /**
   * Generate unique facility ID for this brigade's Role 2 Basic
   * Format: role2_div{X}_brig{Y}
   * 
   * @returns {string|null} Facility ID or null if no parent division
   */
  generateFacilityId() {
    if (!this.parent) return null;
    
    const division = this.parent;
    return `role2_div${division.division_number}_brig${this.brigade_number}`;
  }
  
  /**
   * Generate human-readable facility name
   * 
   * @returns {string} Name like "Division 1 / Brigade 2 (Role 2 Basic)"
   */
  generateFacilityName() {
    if (!this.parent) return `Brigade ${this.brigade_number} Medical Company (Role 2 Basic)`;
    
    const division = this.parent;
    return `Division ${division.division_number} / Brigade ${this.brigade_number} (Role 2 Basic)`;
  }
  
  /**
   * Get Role 2 Basic distance from FEBA
   * 
   * @returns {number} Distance in kilometers
   */
  getRole2Distance() {
    return this.position_feba_km;
  }
  
  // ============================================================================
  // MEDICAL INTEGRATION
  // ============================================================================
  
  /**
   * Link Role 2 Basic medical facility to this brigade
   * Called from Tab 3 when medical structure is built
   * 
   * @param {Role2BasicFacility} facility - Medical facility object
   */
  linkRole2BasicFacility(facility) {
    this.role2_basic_facility = facility;
    this.role2_basic_facility_id = facility.facility_id;
  }
  
  /**
   * Check if brigade has medical support configured
   * 
   * @returns {boolean} True if Role 2 Basic facility linked
   */
  hasMedicalSupport() {
    return this.role2_basic_facility !== null;
  }
  
  /**
   * Get medical facility capacity status
   * 
   * @returns {Object|null} Capacity info or null if no facility
   */
  getMedicalCapacityStatus() {
    if (!this.role2_basic_facility) return null;
    
    return {
      facility_id: this.role2_basic_facility_id,
      facility_name: this.role2_basic_facility.facility_name,
      beds_total: this.role2_basic_facility.beds,
      beds_available: this.role2_basic_facility.getAvailableBeds(),
      bed_utilization: this.role2_basic_facility.getBedUtilization(),
      surgical_teams: this.role2_basic_facility.surgical_teams,
      operational: this.role2_basic_facility.operational
    };
  }
  
  /**
   * Get all Role 1 facilities from battalions
   * Useful for medical planning
   * 
   * @returns {Array} Array of Role 1 facility objects
   */
  getAllBattalionRole1Facilities() {
    return this.battalions
      .filter(bn => bn.role1_facility !== null)
      .map(bn => bn.role1_facility);
  }
  
  // ============================================================================
  // AGGREGATION METHODS
  // ============================================================================
  
  /**
   * Get combat effectiveness status for all battalions
   * 
   * @returns {Object} Summary of battalion effectiveness
   */
  getBattalionEffectivenessStatus() {
    const total = this.battalions.length;
    const effective = this.battalions.filter(bn => bn.isCombatEffective()).length;
    const degraded = this.battalions.filter(bn => {
      const pct = bn.getStrengthPercentage();
      return pct >= 50 && pct < 70;
    }).length;
    const ineffective = this.battalions.filter(bn => {
      const pct = bn.getStrengthPercentage();
      return pct > 0 && pct < 50;
    }).length;
    const destroyed = this.battalions.filter(bn => bn.current_strength === 0).length;
    
    return {
      total_battalions: total,
      combat_effective: effective,
      degraded: degraded,
      ineffective: ineffective,
      destroyed: destroyed,
      effectiveness_percentage: total > 0 ? (effective / total) * 100 : 0
    };
  }
  
  /**
   * Calculate total casualties across all battalions
   * 
   * @returns {number} Total casualties (initial - current strength)
   */
  getTotalBattalionCasualties() {
    return this.battalions.reduce((sum, bn) => {
      return sum + (bn.strength - bn.current_strength);
    }, 0);
  }
  
  /**
   * Get average casualty modifier across battalions
   * Weighted by battalion strength
   * 
   * @returns {number} Weighted average modifier
   */
  getAverageCasualtyModifier() {
    if (this.battalions.length === 0) return 1.0;
    
    const totalStrength = this.battalions.reduce((sum, bn) => sum + bn.strength, 0);
    if (totalStrength === 0) return 1.0;
    
    const weightedSum = this.battalions.reduce((sum, bn) => {
      return sum + (bn.getCasualtyModifier() * bn.strength);
    }, 0);
    
    return weightedSum / totalStrength;
  }
  
  // ============================================================================
  // SERIALIZATION
  // ============================================================================
  
  /**
   * Convert to JSON for storage
   * Includes brigade-specific fields and battalion summary
   * 
   * v3.0 FIX (Feb 7, 2026): Added sub_units and composition_source to serialization
   * Required for v3.0 calculator which uses sub_units for accurate casualty calculations
   * 
   * @returns {Object} JSON representation
   */
  toJSON() {
    return {
      ...super.toJSON(),
      brigade_number: this.brigade_number,
      brigade_type: this.brigade_type,
      // 2026-10-24: ForceService sets unit_type on the instance and toJSON dropped it: a saved or reloaded force had brigades with no unit_type
      // (Tab 1 'Not specified', no brigade type in the calculation). Persisted now (Dr. Tatar); falls back to brigade_type.
      unit_type: this.unit_type || this.brigade_type,
      parent_division_id: this.parent_division_id,
      role2_basic_facility_id: this.role2_basic_facility_id,
      battalions_count: this.battalions.length,
      battalions: this.battalions.map(bn => bn.toJSON()),
      total_battalion_strength: this.getTotalBattalionStrength(),
      battalion_effectiveness: this.getBattalionEffectivenessStatus(),
      // v3.0: Include sub_units for doctrinal composition (added by ForceService)
      sub_units: this.sub_units,
      composition_source: this.composition_source
    };
  }
  
  /**
   * Get detailed summary including battalions and medical status
   * 
   * @returns {Object} Detailed summary
   */
  getDetailedSummary() {
    const base_summary = this.getSummary();
    
    return {
      ...base_summary,
      brigade_number: this.brigade_number,
      brigade_type: this.brigade_type,
      battalions_count: this.battalions.length,
      total_battalion_strength: this.getTotalBattalionStrength(),
      battalion_effectiveness: this.getBattalionEffectivenessStatus(),
      total_battalion_casualties: this.getTotalBattalionCasualties(),
      average_casualty_modifier: this.getAverageCasualtyModifier(),
      medical_support: this.hasMedicalSupport(),
      medical_capacity: this.getMedicalCapacityStatus(),
      role2_basic_facility: {
        id: this.role2_basic_facility_id,
        name: this.generateFacilityName(),
        distance_feba_km: this.getRole2Distance()
      },
      battalions_summary: this.battalions.map(bn => ({
        id: bn.id,
        name: bn.name,
        battalion_number: bn.battalion_number,
        strength: bn.current_strength,
        strength_percentage: bn.getStrengthPercentage(),
        effectiveness: bn.getCombatEffectivenessRating(),
        mission: bn.mission
      }))
    };
  }
}

/**
 * USAGE EXAMPLES:
 * 
 * import { Brigade } from './models/force/Brigade.js';
 * import { Battalion } from './models/force/Battalion.js';
 * 
 * // Create a brigade
 * const brigade = new Brigade({
 *   id: 'brig1',
 *   name: '1st Brigade',
 *   brigade_number: 1,
 *   brigade_type: 'MECHANIZED',
 *   strength: 4000,
 *   position_feba_km: 20
 * });
 * 
 * // Add battalions
 * const bn1 = new Battalion({
 *   id: 'bn1',
 *   name: '1st Battalion',
 *   battalion_number: 1,
 *   strength: 800,
 *   mission: 'MAIN_EFFORT',
 *   is_frontline: true
 * });
 * 
 * const bn2 = new Battalion({
 *   id: 'bn2',
 *   name: '2nd Battalion',
 *   battalion_number: 2,
 *   strength: 800,
 *   mission: 'SUPPORTING'
 * });
 * 
 * brigade.addBattalion(bn1);
 * brigade.addBattalion(bn2);
 * 
 * // Check hierarchy
 * console.log(bn1.getHumanReadablePath());
 * // Output: "1st Brigade → 1st Battalion"
 * 
 * // Get brigade strength
 * console.log(brigade.getTotalBattalionStrength()); // 1600
 * 
 * // Apply casualties to battalion
 * bn1.applyCasualties(200);
 * 
 * // Check brigade effectiveness
 * console.log(brigade.getBattalionEffectivenessStatus());
 * // Output: { total_battalions: 2, combat_effective: 1, degraded: 1, ... }
 * 
 * // Generate facility ID (requires parent division)
 * // division.addBrigade(brigade);
 * // console.log(brigade.generateFacilityId()); // "role2_div1_brig1"
 */
